import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { httpRetryer, tokenEscalation } from '@frontend/util/http-utilities';
import { TokenStorageService } from '@util/token-storage';
import { map, Observable, switchMap, take } from 'rxjs';

import { AuthService } from 'auth';
import { EnvService } from 'frontend-environment';
import { Empty } from 'google-protobuf/google/protobuf/empty_pb';
import {
  AddAssetToLibraryRequest,
  AddAssetToLibraryResponse,
  CreateCreativeGroupParams,
  CreativeAssetParams,
  CreativeCreativeGroupResponse,
  CreativeCreativeResponse,
  CreativeGroup,
  GetAssetsByIdRequest,
  GetAssetsByIdResponse,
  GetCreativeGroupsByIdResponse,
  GetCreativesByIdResponse,
  SearchLibraryAssetsRequest,
  SearchLibraryAssetsResponse,
  SetAssetLibraryVisibilityRequest,
  UpdateAssociatedAssetRequest,
  UpdateCreativeGroupParams,
  UpdateCreativeParams,
  UpdateCreativeResponse,
  ValidateCreativeRequest,
  ValidateCreativeResponse,
} from './interfaces';

@Injectable({ providedIn: 'root' })
export class CreativeAssetLibraryApi {
  private env = inject<
    EnvService<{
      CREATIVE_ASSET_SERVICE_URL: string;
    }>
  >(EnvService);
  private httpClient = inject(HttpClient);
  private authService = inject(AuthService);
  private tokenStorage = inject(TokenStorageService);

  /**
   * Adds an asset to a library by making a POST request to the creative asset service.
   */
  public addAssetToLibrary(body: AddAssetToLibraryRequest) {
    return this.authorize((token) =>
      this.httpClient
        .post<AddAssetToLibraryResponse>(
          `${this.env.CREATIVE_ASSET_SERVICE_URL}/v1/add_asset_to_library`,
          body,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .pipe(httpRetryer({})),
    );
  }

  /**
   * Gets creative groups by an array of ids
   */
  getCreativeGroupsById(ids: string[]) {
    return this.authorize((token) =>
      this.httpClient
        .post<GetCreativeGroupsByIdResponse>(
          `${this.env.CREATIVE_ASSET_SERVICE_URL}/v1/get_creative_groups_by_id`,
          { creativeGroupIds: ids },
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .pipe(httpRetryer({})),
    );
  }

  /**
   * Gets creatives by an array of ids
   */
  getCreativesById(ids: string[]) {
    return this.authorize((token) =>
      this.httpClient
        .post<GetCreativesByIdResponse>(
          `${this.env.CREATIVE_ASSET_SERVICE_URL}/v1/get_creatives_by_id`,
          { creativeIds: ids },
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .pipe(
          httpRetryer({}),
          // we only care about the first version of each creative
          map((resp) => ({
            ...resp,
            creatives: resp.creatives.map((creative) => ({
              ...creative,
              asset: {
                ...creative.asset,
                version: creative.asset.versions
                  ? creative.asset.versions[0]
                  : creative.asset.version,
              },
            })),
          })),
        ),
    );
  }

  /**
   * Creates a creative group by making a POST request to v1/create_creative_group
   */
  createCreativeGroup(createCreativeGroupParams: CreateCreativeGroupParams) {
    return this.authorize((token) =>
      this.httpClient
        .post<CreativeCreativeGroupResponse>(
          `${this.env.CREATIVE_ASSET_SERVICE_URL}/v1/create_creative_group`,
          createCreativeGroupParams,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .pipe(httpRetryer({})),
    );
  }

  /**
   * Updates an existing creative group by making a POST request to the creative asset service.
   */
  updateCreativeGroup(updateCreativeGroupParams: UpdateCreativeGroupParams) {
    return this.authorize((token) =>
      this.httpClient
        .post<CreativeGroup>(
          `${this.env.CREATIVE_ASSET_SERVICE_URL}/v1/update_creative_group`,
          updateCreativeGroupParams,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .pipe(httpRetryer({})),
    );
  }

  /**
   * This method handles the HTTP POST request to update a creative asset with the specified parameters.
   * On successful update, it returns an observable of type `UpdateCreativeResponse`.
   */
  updateCreative(updateCreativeParams: UpdateCreativeParams) {
    return this.authorize((token) =>
      this.httpClient
        .post<UpdateCreativeResponse>(
          `${this.env.CREATIVE_ASSET_SERVICE_URL}/v1/update_creative`,
          updateCreativeParams,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .pipe(
          httpRetryer({}),
          // we only care about the first version of each creative
          map((resp) => ({
            ...resp,
            asset: {
              ...resp.asset,
              version: resp.asset.versions
                ? resp.asset.versions[0]
                : resp.asset.version,
            },
          })),
        ),
    );
  }

  /**
   * Creates a creative asset by making an HTTP POST request /v1/create_creative endpoint.
   */
  createCreative(createAssetParams: CreativeAssetParams) {
    return this.authorize((token) =>
      this.httpClient
        .post<CreativeCreativeResponse>(
          `${this.env.CREATIVE_ASSET_SERVICE_URL}/v1/create_creative`,
          createAssetParams,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .pipe(
          httpRetryer({}),
          // we only care about the first version of each creative
          map((resp) => ({
            ...resp,
            asset: {
              ...resp.asset,
              version: resp.asset.versions
                ? resp.asset.versions[0]
                : resp.asset.version,
            },
          })),
        ),
    );
  }

  /** Retrieves assets based on their ID from the Creative Asset Service. Retries failed requests. */
  getAssetById(getAssetsParams: GetAssetsByIdRequest) {
    return this.authorize((token) =>
      this.httpClient
        .post<GetAssetsByIdResponse>(
          `${this.env.CREATIVE_ASSET_SERVICE_URL}/v1/get_assets_by_id`,
          getAssetsParams,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .pipe(
          httpRetryer({}),
          // we only care about the first version of each creative
          map((resp) => ({
            ...resp,
            assets: resp.assets.map((asset) => ({
              ...asset,
              version: asset.versions ? asset.versions[0] : asset.version,
            })),
          })),
        ),
    );
  }

  /**
   * Searches library assets based on specified criteria.
   * The method accepts a query object that can specify various search filters
   */
  searchLibraryAssets(query: SearchLibraryAssetsRequest) {
    return this.authorize((token) =>
      this.httpClient
        .post<SearchLibraryAssetsResponse>(
          `${this.env.CREATIVE_ASSET_SERVICE_URL}/v1/search_library_assets`,
          {
            startRequest: {
              ownerIds: {},
              sortDirection: 'CREATED_AT_DESC',
              ...query,
            },
          },
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .pipe(
          httpRetryer({}),
          // we only care about the first version of each creative
          map((resp) => ({
            ...resp,
            assets: resp.records.map((asset) => ({
              ...asset,
              version: asset.versions ? asset.versions[0] : asset.version,
            })),
          })),
        ),
    );
  }

  /**
   * Sets.
   * The method accepts a query object that can specify various search filters
   */
  setAssetLibraryVisibility(request: SetAssetLibraryVisibilityRequest) {
    return this.authorize((token) =>
      this.httpClient
        .post<Empty>(
          `${this.env.CREATIVE_ASSET_SERVICE_URL}/v1/set_asset_library_visibility`,
          request,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .pipe(httpRetryer({})),
    );
  }

  validateCreative(request: ValidateCreativeRequest) {
    return this.authorize((token) =>
      this.httpClient
        .post<ValidateCreativeResponse>(
          `${this.env.CREATIVE_ASSET_SERVICE_URL}/v1/validate_creative`,
          request,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .pipe(httpRetryer({})),
    );
  }

  updateAssociatedAsset(request: UpdateAssociatedAssetRequest) {
    return this.authorize((token) =>
      this.httpClient
        .post<Empty>(
          `${this.env.CREATIVE_ASSET_SERVICE_URL}/v1/update_associated_asset`,
          request,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .pipe(httpRetryer({})),
    );
  }

  /**
   * The method first retrieves a base authentication token from `authService.myTerminusToken$`,
   * then applies `tokenEscalation` which sends this token to /v1/authorize which is a token escalation endpoint to receive an escalated token.
   * This escalated token is then used by the `source` function to make authorized API calls
   * */
  private authorize<T>(source: (token: string | undefined) => Observable<T>) {
    return this.authService.myTerminusToken$.pipe(
      switchMap((authToken) =>
        tokenEscalation({
          baseToken: authToken,
          tokenEscalationUrl: `${this.env.CREATIVE_ASSET_SERVICE_URL}/v1/authorize`,
          httpClient: this.httpClient,
          source,
          tokenStorage: this.tokenStorage,
        }),
      ),
      take(1),
    );
  }
}
